// The codes and answer sets the validated-tool instruments draw on: LOINC
// answer lists for the tools LOINC codes item by item, and our own question
// and answer codes for the rest. Data only, so the terminology modules can
// spread it in without importing the templates.
import type {
  CodeSystemConcept,
  ConceptMapGroupElement,
  ValueSet,
  ValueSetComposeIncludeConcept,
} from '@medplum/fhirtypes'
import { LOINC_CODE_SYSTEM, SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { ITEM_WEIGHT_EXTENSION_URL } from '#src/structureDefinitions.ts'
import {
  answerConcepts,
  answerValueSet,
  captureProperties,
  loincRelatedProperties,
  MANAGED_BY_CODE,
  VALUE_SET_BASE,
} from '#src/terminology/define.ts'
import { ASSESSMENT_ANSWERS_SYSTEM } from '#src/terminology/types.ts'

export const INSTRUMENT_BANDS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/instrument-bands`

interface ScoredAnswer {
  code: string
  display: string
  // Absent for an answer that is recorded but not scored.
  score?: number
}

function scoredAnswerSet(
  slug: string,
  title: string,
  system: string,
  answers: ScoredAnswer[]
): ValueSet {
  return answerValueSet(
    slug,
    title,
    system,
    answers.map(({ code, display, score }) => {
      const concept: ValueSetComposeIncludeConcept = { code, display }
      if (score !== undefined) {
        concept.extension = [
          { url: ITEM_WEIGHT_EXTENSION_URL, valueDecimal: score },
        ]
      }
      return concept
    })
  )
}

// LOINC-coded tools: PHQ-9, PHQ-2, GAD-7, STEADI.

export const PHQ_FREQUENCY_ANSWERS = `${VALUE_SET_BASE}/phq-frequency`
export const GAD7_WORRY_CONTROL_ANSWERS = `${VALUE_SET_BASE}/gad-7-worry-control-frequency`
export const PHQ_DIFFICULTY_ANSWERS = `${VALUE_SET_BASE}/phq-difficulty`
export const STEADI_YES_NO_2_ANSWERS = `${VALUE_SET_BASE}/steadi-yes-no-2`
export const STEADI_YES_NO_1_ANSWERS = `${VALUE_SET_BASE}/steadi-yes-no-1`

// Our own codes: AD8, Braden, Mini-Cog, Katz ADL, Lawton IADL, and the
// GAD-2 panel and total.

export const AD8_ANSWERS = `${VALUE_SET_BASE}/ad8-change`
export const BRADEN_SENSORY_PERCEPTION_ANSWERS = `${VALUE_SET_BASE}/braden-sensory-perception`
export const BRADEN_MOISTURE_ANSWERS = `${VALUE_SET_BASE}/braden-moisture`
export const BRADEN_ACTIVITY_ANSWERS = `${VALUE_SET_BASE}/braden-activity`
export const BRADEN_MOBILITY_ANSWERS = `${VALUE_SET_BASE}/braden-mobility`
export const BRADEN_NUTRITION_ANSWERS = `${VALUE_SET_BASE}/braden-nutrition`
export const BRADEN_FRICTION_AND_SHEAR_ANSWERS = `${VALUE_SET_BASE}/braden-friction-and-shear`
export const MINI_COG_WORD_LIST_ANSWERS = `${VALUE_SET_BASE}/mini-cog-word-list`
export const MINI_COG_RECALL_ANSWERS = `${VALUE_SET_BASE}/mini-cog-word-recalled`
export const MINI_COG_CLOCK_ANSWERS = `${VALUE_SET_BASE}/mini-cog-clock-drawing`
export const ADL_INDEPENDENCE_ANSWERS = `${VALUE_SET_BASE}/adl-independence`
export const MOCA_EDUCATION_ANSWERS = `${VALUE_SET_BASE}/moca-education`
export const SLUMS_EDUCATION_ANSWERS = `${VALUE_SET_BASE}/slums-education`

// Domain subscores entered as numbers: [code suffix, display, max].
export const MOCA_DOMAINS = [
  [
    'visuospatial-executive',
    'Visuospatial/executive',
    5,
    'Trail making 1, cube copy 1, clock (contour, numbers, hands) 3',
  ],
  ['naming', 'Naming', 3, '1 per animal named: lion, rhinoceros, camel'],
  [
    'attention',
    'Attention',
    6,
    'Digits forward 1, backward 1; letter tapping 1 (0 if 2+ errors); serial 7s 3 (4–5 right 3, 2–3 right 2, 1 right 1)',
  ],
  [
    'language',
    'Language',
    3,
    'Sentence repetition 1 each (2); 11+ F-words in one minute 1',
  ],
  ['abstraction', 'Abstraction', 2, '1 per pair: train–bicycle, watch–ruler'],
  [
    'delayed-recall',
    'Delayed recall',
    5,
    '1 per word recalled without a cue: face, velvet, church, daisy, red',
  ],
  [
    'orientation',
    'Orientation',
    6,
    '1 each: date, month, year, day, place, city',
  ],
] as const

export const SLUMS_DOMAINS = [
  ['orientation', 'Orientation', 3, '1 each: day of the week, year, state'],
  ['calculation', 'Calculation', 3, 'Amount spent 1; amount left 2'],
  [
    'animal-fluency',
    'Animal naming fluency',
    3,
    'Animals in one minute: 0–4 → 0, 5–9 → 1, 10–14 → 2, 15+ → 3',
  ],
  [
    'recall',
    'Recall of five objects',
    5,
    '1 per object recalled: apple, pen, tie, house, car',
  ],
  ['digits-backward', 'Digit span backward', 2, '87 → 0, 648 → 1, 8537 → 1'],
  [
    'clock-drawing',
    'Clock drawing',
    4,
    'Hour markers placed 2; time (ten to eleven) correct 2',
  ],
  [
    'figure-recognition',
    'Figure recognition',
    2,
    'X in the triangle 1; largest figure 1',
  ],
  [
    'story-recall',
    'Story recall',
    8,
    '2 each: name, work, when she went back to work, state she lived in',
  ],
] as const

export const MMSE_DOMAINS = [
  [
    'orientation-time',
    'Orientation to time',
    5,
    '1 each: year, season, date, day, month',
  ],
  [
    'orientation-place',
    'Orientation to place',
    5,
    '1 each: state, county, town, building, floor',
  ],
  [
    'registration',
    'Registration',
    3,
    '1 per object repeated on the first trial',
  ],
  [
    'attention-calculation',
    'Attention and calculation',
    5,
    'Serial 7s from 100, or WORLD spelled backward: 1 per correct step',
  ],
  ['recall', 'Recall', 3, '1 per object recalled'],
  [
    'language-praxis',
    'Language and praxis',
    9,
    'Name pencil and watch 2, repetition 1, three-stage command 3, read and obey 1, write a sentence 1, copy pentagons 1',
  ],
] as const

const IADL_ITEMS = [
  'phone-use',
  'shopping',
  'food-preparation',
  'housekeeping',
  'laundry',
  'mode-of-transportation',
  'medication-responsibility',
  'financial-management',
] as const

export type IadlItem = (typeof IADL_ITEMS)[number]

export function iadlAnswers(item: IadlItem): string {
  return `${VALUE_SET_BASE}/iadl-${item}`
}

const IADL_ANSWERS: Record<IadlItem, [display: string, score: number][]> = {
  'phone-use': [
    [
      'Operates telephone on own initiative-looks up and dials numbers, etc.',
      1,
    ],
    ['Dials a few well-known numbers.', 1],
    ['Answers telephone but does not dial.', 1],
    ['Does not use telephone at all.', 0],
  ],
  shopping: [
    ['Takes care of all shopping needs independently.', 1],
    ['Shops independently for small purchases.', 0],
    ['Needs to be accompanied on any shopping trip.', 0],
    ['Completely unable to shop.', 0],
  ],
  'food-preparation': [
    ['Plans, prepares and serves adequate meals independently', 1],
    ['Prepares adequate meals if supplied with ingredients', 0],
    [
      'Heats, serves and prepares meals, or prepares meals but does not maintain adequate diet',
      0,
    ],
    ['Needs to have meals prepared and served', 0],
  ],
  housekeeping: [
    ['Maintains house alone or with occasional assistance', 1],
    ['Performs light daily tasks such as dishwashing, bed making', 1],
    [
      'Performs light daily tasks but cannot maintain acceptable level of cleanliness',
      1,
    ],
    ['Needs help with all home maintenance tasks', 1],
    ['Does not participate in any housekeeping tasks', 0],
  ],
  laundry: [
    ['Does personal laundry completely', 1],
    ['Launders small items—rinses stockings, etc.', 1],
    ['All laundry must be done by others', 0],
  ],
  'mode-of-transportation': [
    ['Travels independently on public transportation or drives own car', 1],
    [
      'Arranges own travel via taxi, but does not otherwise use public transportation',
      1,
    ],
    ['Travels on public transportation when accompanied by another', 1],
    ['Travel limited to taxi or automobile with assistance of another', 0],
    ['Does not travel at all', 0],
  ],
  'medication-responsibility': [
    [
      'Is responsible for taking medication in correct dosages at correct time',
      1,
    ],
    [
      'Takes responsibility if medication is prepared in advance in separate dosages',
      0,
    ],
    ['Is not capable of dispensing own medication', 0],
  ],
  'financial-management': [
    ['Manages financial matters independently', 1],
    [
      'Manages day-to-day purchases, but needs help with banking, major purchases, etc.',
      1,
    ],
    ['Incapable of handling money', 0],
  ],
}

// Lawton's answers keep the codes today's questionnaire gave them, numbered
// in the order the form lists them.
function iadlAnswerList(item: IadlItem): ScoredAnswer[] {
  return IADL_ANSWERS[item].map(([display, score], index) => ({
    code: `iadl-${item}-${index + 1}`,
    display,
    score,
  }))
}

const OWN_ANSWER_LISTS: [
  slug: string,
  title: string,
  answers: ScoredAnswer[],
][] = [
  [
    'ad8-change',
    'AD8 change',
    [
      { code: 'ad8-yes', display: 'Yes, a change', score: 1 },
      { code: 'ad8-no', display: 'No, no change', score: 0 },
      // "Don't know": scores nothing, yet answers the item, so it never
      // holds back the total.
      { code: 'ad8-na', display: "N/A, don't know", score: 0 },
    ],
  ],
  [
    'braden-sensory-perception',
    'Braden sensory perception',
    [
      {
        code: 'braden-completely-limited',
        display: 'Completely limited',
        score: 1,
      },
      { code: 'braden-very-limited', display: 'Very limited', score: 2 },
      {
        code: 'braden-slightly-limited',
        display: 'Slightly limited',
        score: 3,
      },
      { code: 'braden-no-impairment', display: 'No impairment', score: 4 },
    ],
  ],
  [
    'braden-moisture',
    'Braden moisture',
    [
      {
        code: 'braden-constantly-moist',
        display: 'Constantly moist',
        score: 1,
      },
      { code: 'braden-very-moist', display: 'Very moist', score: 2 },
      {
        code: 'braden-occasionally-moist',
        display: 'Occasionally moist',
        score: 3,
      },
      { code: 'braden-rarely-moist', display: 'Rarely moist', score: 4 },
    ],
  ],
  [
    'braden-activity',
    'Braden activity',
    [
      { code: 'braden-bedfast', display: 'Bedfast', score: 1 },
      { code: 'braden-chairfast', display: 'Chairfast', score: 2 },
      {
        code: 'braden-walks-occasionally',
        display: 'Walks occasionally',
        score: 3,
      },
      {
        code: 'braden-walks-frequently',
        display: 'Walks frequently',
        score: 4,
      },
    ],
  ],
  [
    'braden-mobility',
    'Braden mobility',
    [
      {
        code: 'braden-completely-immobile',
        display: 'Completely immobile',
        score: 1,
      },
      { code: 'braden-very-limited', display: 'Very limited', score: 2 },
      {
        code: 'braden-slightly-limited',
        display: 'Slightly limited',
        score: 3,
      },
      { code: 'braden-no-limitations', display: 'No limitations', score: 4 },
    ],
  ],
  [
    'braden-nutrition',
    'Braden nutrition',
    [
      { code: 'braden-very-poor', display: 'Very poor', score: 1 },
      {
        code: 'braden-probably-inadequate',
        display: 'Probably inadequate',
        score: 2,
      },
      { code: 'braden-adequate', display: 'Adequate', score: 3 },
      { code: 'braden-excellent', display: 'Excellent', score: 4 },
    ],
  ],
  [
    'braden-friction-and-shear',
    'Braden friction and shear',
    [
      { code: 'braden-problem', display: 'Problem', score: 1 },
      {
        code: 'braden-potential-problem',
        display: 'Potential problem',
        score: 2,
      },
      {
        code: 'braden-no-apparent-problem',
        display: 'No apparent problem',
        score: 3,
      },
    ],
  ],
  [
    'mini-cog-word-list',
    'Mini-Cog word list',
    [
      ['1', 'Banana, Sunrise, Chair'],
      ['2', 'Leader, Season, Table'],
      ['3', 'Village, Kitchen, Baby'],
      ['4', 'River, Nation, Finger'],
      ['5', 'Captain, Garden, Picture'],
      ['6', 'Daughter, Heaven, Mountain'],
    ].map(([version, words]) => ({
      code: `mini-cog-word-list-${version}`,
      display: `Version ${version}: ${words}`,
    })),
  ],
  [
    'mini-cog-word-recalled',
    'Mini-Cog word recalled',
    [
      { code: 'mini-cog-yes', display: 'Recalled', score: 1 },
      { code: 'mini-cog-no', display: 'Not recalled', score: 0 },
    ],
  ],
  [
    'mini-cog-clock-drawing',
    'Mini-Cog clock drawing',
    [
      { code: 'mini-cog-normal', display: 'Normal', score: 2 },
      { code: 'mini-cog-abnormal', display: 'Abnormal', score: 0 },
    ],
  ],
  [
    'adl-independence',
    'Katz ADL independence',
    [
      { code: 'adl-independent', display: 'Independent', score: 1 },
      { code: 'adl-dependent', display: 'Dependent', score: 0 },
    ],
  ],
  [
    'moca-education',
    'MoCA 12 years or fewer of formal education',
    [
      { code: 'moca-education-yes', display: 'Yes', score: 1 },
      { code: 'moca-education-no', display: 'No', score: 0 },
    ],
  ],
  [
    'slums-education',
    'SLUMS high school education',
    [
      { code: 'slums-education-yes', display: 'Yes' },
      { code: 'slums-education-no', display: 'No' },
    ],
  ],
  ...IADL_ITEMS.map((item): [string, string, ScoredAnswer[]] => [
    `iadl-${item}`,
    `Lawton IADL ${item.replaceAll('-', ' ')}`,
    iadlAnswerList(item),
  ]),
]

const PHQ_FREQUENCY: ScoredAnswer[] = [
  { code: 'LA6568-5', display: 'Not at all', score: 0 },
  { code: 'LA6569-3', display: 'Several days', score: 1 },
  { code: 'LA6570-1', display: 'More than half the days', score: 2 },
  { code: 'LA6571-9', display: 'Nearly every day', score: 3 },
]

export const INSTRUMENT_ANSWER_SETS: ValueSet[] = [
  scoredAnswerSet(
    'phq-frequency',
    'PHQ frequency, last 2 weeks',
    LOINC_CODE_SYSTEM,
    PHQ_FREQUENCY
  ),
  // GAD-7 item 2 offers "More days than not" in place of "More than half
  // the days", scored the same.
  scoredAnswerSet(
    'gad-7-worry-control-frequency',
    'GAD-7 worry control frequency',
    LOINC_CODE_SYSTEM,
    [
      PHQ_FREQUENCY[0],
      PHQ_FREQUENCY[1],
      { code: 'LA18938-3', display: 'More days than not', score: 2 },
      PHQ_FREQUENCY[3],
    ]
  ),
  scoredAnswerSet(
    'phq-difficulty',
    'PHQ functional difficulty',
    LOINC_CODE_SYSTEM,
    [
      { code: 'LA6572-7', display: 'Not difficult at all' },
      { code: 'LA6573-5', display: 'Somewhat difficult' },
      { code: 'LA6575-0', display: 'Very difficult' },
      { code: 'LA6574-3', display: 'Extremely difficult' },
    ]
  ),
  // STEADI weighs a fall and a walking aid twice; the same LOINC yes/no
  // therefore needs two value sets.
  scoredAnswerSet(
    'steadi-yes-no-2',
    'STEADI yes/no, weighted 2',
    LOINC_CODE_SYSTEM,
    [
      { code: 'LA33-6', display: 'Yes', score: 2 },
      { code: 'LA32-8', display: 'No', score: 0 },
    ]
  ),
  scoredAnswerSet(
    'steadi-yes-no-1',
    'STEADI yes/no, weighted 1',
    LOINC_CODE_SYSTEM,
    [
      { code: 'LA33-6', display: 'Yes', score: 1 },
      { code: 'LA32-8', display: 'No', score: 0 },
    ]
  ),
  ...OWN_ANSWER_LISTS.map(([slug, title, answers]) =>
    scoredAnswerSet(slug, title, ASSESSMENT_ANSWERS_SYSTEM, answers)
  ),
]

// Each answer code once, though Braden's mobility and sensory lists share
// two of them.
export const INSTRUMENT_ANSWER_CONCEPTS = answerConcepts(
  OWN_ANSWER_LISTS.flatMap(([, , answers]) => answers).map(
    ({ code, display }): [string, string] => [code, display]
  ),
  { displays: 'unique' }
)

interface OwnCode {
  code: string
  display: string
  // Absent for a panel, which is not recorded on its own.
  scale?: 'Ord' | 'Nom' | 'Qn'
  answerSet?: string
  loinc?: string
}

function ownCodeConcept({
  code,
  display,
  scale,
  answerSet,
  loinc,
}: OwnCode): CodeSystemConcept {
  return {
    code,
    display,
    property: [
      ...loincRelatedProperties(loinc),
      MANAGED_BY_CODE,
      ...(scale === 'Qn'
        ? captureProperties({ kind: 'quantity', unit: '{score}' })
        : []),
      ...(scale && answerSet
        ? captureProperties({
            kind: 'choice',
            answerSet,
            ordered: scale === 'Ord',
          })
        : []),
    ],
  }
}

function domainCodes(
  tool: string,
  label: string,
  domains: readonly (readonly [string, string, number, string])[]
): OwnCode[] {
  return domains.map(([item, display]) => ({
    code: `${tool}-${item}`,
    display: `${label} ${display.toLowerCase()}`,
    scale: 'Qn',
  }))
}

const OWN_CODES: OwnCode[] = [
  {
    code: 'gad-2-panel',
    display: 'Generalized anxiety disorder 2 item (GAD-2)',
  },
  { code: 'gad-2-total', display: 'GAD-2 total score', scale: 'Qn' },
  { code: 'ad8-panel', display: 'AD8 dementia screening', loinc: '71945-0' },
  ...[
    ['judgment', 'Problems with judgment'],
    ['hobbies', 'Less interest in hobbies/activities'],
    ['repeats', 'Repeats the same things over and over'],
    [
      'learning-tools',
      'Trouble learning how to use a tool, appliance, or gadget',
    ],
    ['month-year', 'Forgets correct month or year'],
    ['finances', 'Trouble handling complicated financial affairs'],
    ['appointments', 'Trouble remembering appointments'],
    ['daily-problems', 'Daily problems with thinking and/or memory'],
  ].map(([item, display]): OwnCode => ({
    code: `ad8-${item}`,
    display,
    scale: 'Ord',
    answerSet: AD8_ANSWERS,
  })),
  {
    code: 'ad8-total',
    display: 'AD8 total score',
    scale: 'Qn',
    loinc: '71722-3',
  },
  { code: 'braden-panel', display: 'Braden scale', loinc: '38228-3' },
  {
    code: 'braden-sensory-perception',
    display: 'Braden sensory perception',
    scale: 'Ord',
    answerSet: BRADEN_SENSORY_PERCEPTION_ANSWERS,
    loinc: '38222-6',
  },
  {
    code: 'braden-moisture',
    display: 'Braden moisture',
    scale: 'Ord',
    answerSet: BRADEN_MOISTURE_ANSWERS,
    loinc: '38229-1',
  },
  {
    code: 'braden-activity',
    display: 'Braden activity',
    scale: 'Ord',
    answerSet: BRADEN_ACTIVITY_ANSWERS,
    loinc: '38223-4',
  },
  {
    code: 'braden-mobility',
    display: 'Braden mobility',
    scale: 'Ord',
    answerSet: BRADEN_MOBILITY_ANSWERS,
    loinc: '38224-2',
  },
  {
    code: 'braden-nutrition',
    display: 'Braden nutrition',
    scale: 'Ord',
    answerSet: BRADEN_NUTRITION_ANSWERS,
    loinc: '38225-9',
  },
  {
    code: 'braden-friction-and-shear',
    display: 'Braden friction and shear',
    scale: 'Ord',
    answerSet: BRADEN_FRICTION_AND_SHEAR_ANSWERS,
    loinc: '38226-7',
  },
  {
    code: 'braden-total',
    display: 'Braden total score',
    scale: 'Qn',
    loinc: '38227-5',
  },
  { code: 'mini-cog-panel', display: 'Mini-Cog', loinc: '72234-8' },
  {
    code: 'mini-cog-word-list-version',
    display: 'Mini-Cog word list version',
    scale: 'Nom',
    answerSet: MINI_COG_WORD_LIST_ANSWERS,
  },
  ...[1, 2, 3].map((word): OwnCode => ({
    code: `mini-cog-word-${word}-recalled`,
    display: `Mini-Cog word ${word} recalled`,
    scale: 'Ord',
    answerSet: MINI_COG_RECALL_ANSWERS,
  })),
  {
    code: 'mini-cog-clock-drawing',
    display: 'Mini-Cog clock drawing',
    scale: 'Ord',
    answerSet: MINI_COG_CLOCK_ANSWERS,
  },
  {
    code: 'mini-cog-total',
    display: 'Mini-Cog total score',
    scale: 'Qn',
    loinc: '72233-0',
  },
  ...domainCodes('moca', 'MoCA', MOCA_DOMAINS),
  {
    code: 'moca-education',
    display: 'MoCA 12 years or fewer of formal education',
    scale: 'Nom',
    answerSet: MOCA_EDUCATION_ANSWERS,
  },
  {
    code: 'moca-memory-index-score',
    display: 'MoCA memory index score',
    scale: 'Qn',
  },
  ...domainCodes('slums', 'SLUMS', SLUMS_DOMAINS),
  {
    code: 'slums-education',
    display: 'SLUMS high school education',
    scale: 'Nom',
    answerSet: SLUMS_EDUCATION_ANSWERS,
  },
  ...domainCodes('mmse', 'MMSE', MMSE_DOMAINS),
  { code: 'adl-panel', display: 'Katz index of independence in ADL' },
  ...[
    'bathing',
    'dressing',
    'toileting',
    'transferring',
    'continence',
    'feeding',
  ].map((item): OwnCode => ({
    code: `adl-${item}`,
    display: `Katz ADL ${item}`,
    scale: 'Ord',
    answerSet: ADL_INDEPENDENCE_ANSWERS,
  })),
  { code: 'adl-total', display: 'Katz ADL total score', scale: 'Qn' },
  {
    code: 'iadl-panel',
    display: 'Lawton instrumental activities of daily living',
  },
  ...IADL_ITEMS.map((item): OwnCode => ({
    code: `iadl-${item}`,
    display: `Lawton IADL ${item.replaceAll('-', ' ')}`,
    scale: 'Ord',
    answerSet: iadlAnswers(item),
  })),
  { code: 'iadl-total', display: 'Lawton IADL total score', scale: 'Qn' },
]

export const INSTRUMENT_QUESTION_CONCEPTS: CodeSystemConcept[] =
  OWN_CODES.map(ownCodeConcept)

// Every LOINC code here was looked up on tx.fhir.org (LOINC 2.82).
export const INSTRUMENT_QUESTION_LOINC_MAP: ConceptMapGroupElement[] =
  OWN_CODES.flatMap(({ code, loinc }) =>
    loinc
      ? [{ code, target: [{ code: loinc, equivalence: 'equivalent' }] }]
      : []
  )
